import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Boards } from "@air/api";
import { InlineInput } from "@air/component-inline-input";
import { Plus } from "@air/next-icons";
import { IconButton } from "@air/primitive-icon-button";
import { Tooltip } from "@air/primitive-tooltip";
import { tailwindMerge } from "@air/tailwind-variants";
import { memo, useCallback, useMemo, useState } from "react";
import { PrivateBoardSearchHeader } from "~/components/BoardSearch/PrivateBoardSearchHeader";
import { useLibraries } from "~/components/LibraryBeta/hooks/queries/useLibraries";
import { useGeneralLibraryTitle } from "~/components/LibraryBeta/hooks/useGeneralLibraryTitle";
import { SimpleSearchBar } from "~/components/SimpleSearchBar";
import { useFetchObjectsPermissions } from "~/hooks/useFetchObjectsPermissions";
import { usePrivateAddOnFeatures } from "~/hooks/usePrivateAddOnFeatures";
import { useCurrentWorkspacePermissionsContext } from "~/providers/CurrentWorkspacePermissionsProvider";
import { useCurrentWorkspace } from "~/providers/CurrentWorkspaceProvider";
import { useCreateNewBoard } from "~/swr-hooks/boards/useCreateNewBoard";
import { useRootWorkspaceBoards } from "~/swr-hooks/boards/useRootWorkspaceBoards";
import { reportErrorToBugsnag } from "~/utils/ErrorUtils";
import { canCreateBoard } from "~/utils/permissions/boardPermissions";
import { BoardSearchListItem } from "./BoardSearchListItem";
import { LibraryListItem } from "./LibraryListItem";
import { getLibraryBoards, getUpdatedLibraryBoards } from "./util";
export const PrivateBoardSearch = /*#__PURE__*/ memo((param)=>{
    let { className, initialBoardId, getIsBoardDisabled, isBoardSelected, onBoardSelectChange, shouldShowSubBoards, shouldShowLibraryBoards, shouldExpandLibraryInsteadOfSelect = false, getIsLibraryDisabled, onSelectLibrary, isLibrarySelected, children } = param;
    const { createNewBoard } = useCreateNewBoard();
    const { currentWorkspace } = useCurrentWorkspace();
    const { data: workspaceBoardsResponse } = useRootWorkspaceBoards(currentWorkspace === null || currentWorkspace === void 0 ? void 0 : currentWorkspace.id);
    const { generalLibraryTitle } = useGeneralLibraryTitle();
    const workspaceBoards = workspaceBoardsResponse || [];
    const { data: libraries } = useLibraries();
    const flattenedLibraries = useMemo(()=>libraries === null || libraries === void 0 ? void 0 : libraries.pages.flatMap((page)=>page.data), [
        libraries
    ]);
    const [searchTerm, setSearchTerm] = useState("");
    const [availableBoards, setAvailableBoards] = useState([]);
    const [availableLibraryBoards, setAvailableLibraryBoards] = useState({});
    const [isCreatingBoard, setIsCreatingBoard] = useState(false);
    const librariesBoardsKeys = Object.keys(availableLibraryBoards);
    const { libraries: librariesFlag } = usePrivateAddOnFeatures();
    const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
    const workspaceId = currentWorkspace === null || currentWorkspace === void 0 ? void 0 : currentWorkspace.id;
    const _availableLibraryBoards = Object.values(availableLibraryBoards).flat();
    useFetchObjectsPermissions({
        objects: {
            boardIds: [
                ...availableBoards,
                ...workspaceBoards,
                ..._availableLibraryBoards
            ].map((board)=>board.id),
            libraryIds: flattenedLibraries === null || flattenedLibraries === void 0 ? void 0 : flattenedLibraries.map((library)=>library.id)
        }
    });
    const handleTermChange = useCallback(async (search)=>{
        try {
            if (!workspaceId) {
                throw new Error("No current workspace");
            }
            const { data: boards } = search ? await Boards.list({
                limit: 10,
                includeAncestors: true,
                search,
                onlyEditable: true,
                workspaceId
            }) : {
                data: []
            };
            if (librariesFlag) {
                setAvailableBoards(boards.filter((b)=>!b.library));
                setAvailableLibraryBoards(getLibraryBoards(boards));
            } else {
                setAvailableBoards(boards);
            }
        } catch (error) {
            reportErrorToBugsnag({
                error,
                context: "FAiled to load boards in PrivateBoardSearch",
                metadata: {
                    search
                }
            });
        }
    }, [
        librariesFlag,
        workspaceId
    ]);
    const createBoard = useCallback(async (boardName)=>{
        const newBoard = await createNewBoard({
            board: {
                title: boardName
            },
            trackLocation: "board-select-modal",
            shouldFollow: true
        });
        if (!newBoard) return;
        setAvailableBoards((boards)=>[
                newBoard,
                ...boards
            ]);
        if (librariesFlag) {
            setAvailableLibraryBoards((prev)=>getUpdatedLibraryBoards(prev, newBoard));
        }
        onBoardSelectChange({
            board: newBoard,
            selected: true
        });
    }, [
        createNewBoard,
        onBoardSelectChange,
        setAvailableBoards,
        setAvailableLibraryBoards,
        librariesFlag
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: tailwindMerge("h-full", className),
        "data-testid": "PRIVATE_BOARD_SEARCH",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "mb-3",
                children: /*#__PURE__*/ _jsx(SimpleSearchBar, {
                    onSearchTermChange: (term)=>{
                        setSearchTerm(term);
                        handleTermChange(term);
                    },
                    placeholder: "Search ".concat((currentWorkspace === null || currentWorkspace === void 0 ? void 0 : currentWorkspace.name) || ""),
                    name: "search-boards"
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "workspace-boards h-[320px] overflow-y-auto rounded border border-grey-7 p-3",
                children: [
                    searchTerm && (availableBoards || librariesBoardsKeys.length > 0) ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            (availableBoards === null || availableBoards === void 0 ? void 0 : availableBoards.length) > 0 && librariesFlag && /*#__PURE__*/ _jsx(PrivateBoardSearchHeader, {
                                title: librariesFlag && generalLibraryTitle ? generalLibraryTitle : "Workspace Boards"
                            }),
                            availableBoards === null || availableBoards === void 0 ? void 0 : availableBoards.map((b)=>/*#__PURE__*/ _jsx(BoardSearchListItem, {
                                    shouldShowSubBoards: shouldShowSubBoards,
                                    isBoardSelected: isBoardSelected,
                                    onBoardSelectChange: onBoardSelectChange,
                                    board: b,
                                    getIsBoardDisabled: getIsBoardDisabled
                                }, b.id)),
                            librariesBoardsKeys.length > 0 && librariesFlag && /*#__PURE__*/ _jsx("div", {
                                style: {
                                    marginTop: availableBoards.length > 0 ? 20 : 0
                                },
                                children: /*#__PURE__*/ _jsx(PrivateBoardSearchHeader, {
                                    title: "Libraries"
                                })
                            }),
                            librariesBoardsKeys.map((libraryId)=>{
                                const libraryBoards = availableLibraryBoards[libraryId];
                                const library = libraryBoards[0].library;
                                if (!library) return null;
                                return /*#__PURE__*/ _jsx(LibraryListItem, {
                                    library: library,
                                    isLibrarySelected: isLibrarySelected,
                                    onSelectLibrary: onSelectLibrary,
                                    getIsLibraryDisabled: getIsLibraryDisabled,
                                    shouldShowSubBoards: shouldShowSubBoards,
                                    shouldShowLibraryBoards: ()=>true,
                                    isBoardSelected: isBoardSelected,
                                    onBoardSelectChange: onBoardSelectChange,
                                    getIsBoardDisabled: getIsBoardDisabled,
                                    initialBoardId: initialBoardId,
                                    libraryBoards: libraryBoards,
                                    shouldExpandLibraryInsteadOfSelect: shouldExpandLibraryInsteadOfSelect
                                }, library.id);
                            })
                        ]
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(PrivateBoardSearchHeader, {
                                title: librariesFlag && generalLibraryTitle ? generalLibraryTitle : "Workspace Boards",
                                rightAdornment: canCreateBoard(workspacePermissions) && /*#__PURE__*/ _jsx(Tooltip, {
                                    label: "Create new board",
                                    side: "right",
                                    children: /*#__PURE__*/ _jsx(IconButton, {
                                        "data-testid": "PRIVATE_BOARD_SEARCH_CREATE_ROOT_BOARD_BUTTON",
                                        icon: Plus,
                                        onClick: ()=>setIsCreatingBoard(true),
                                        size: "small",
                                        disabled: isCreatingBoard,
                                        appearance: "ghost",
                                        color: "grey",
                                        label: "Create New Board"
                                    })
                                })
                            }),
                            isCreatingBoard && /*#__PURE__*/ _jsx(InlineInput, {
                                rows: 1,
                                allowNewLines: false,
                                label: "Board name",
                                className: "mb-2",
                                inputClassName: "w-full",
                                "data-testid": "PRIVATE_BOARD_SEARCH_CREATE_ROOT_BOARD_EDITABLE_TEXT",
                                onSubmit: async (boardName)=>{
                                    setIsCreatingBoard(false);
                                    if (boardName) {
                                        await createBoard(boardName);
                                    }
                                }
                            }),
                            workspaceBoards.map((b)=>/*#__PURE__*/ _jsx(BoardSearchListItem, {
                                    initialBoardId: initialBoardId,
                                    shouldShowSubBoards: shouldShowSubBoards,
                                    isBoardSelected: isBoardSelected,
                                    onBoardSelectChange: onBoardSelectChange,
                                    board: b,
                                    getIsBoardDisabled: getIsBoardDisabled
                                }, b.id)),
                            librariesFlag && !!(flattenedLibraries === null || flattenedLibraries === void 0 ? void 0 : flattenedLibraries.length) && /*#__PURE__*/ _jsxs("div", {
                                className: "mt-4",
                                children: [
                                    /*#__PURE__*/ _jsx(PrivateBoardSearchHeader, {
                                        title: "Libraries"
                                    }),
                                    flattenedLibraries.map((library)=>/*#__PURE__*/ _jsx(LibraryListItem, {
                                            library: library,
                                            isLibrarySelected: isLibrarySelected,
                                            getIsLibraryDisabled: getIsLibraryDisabled,
                                            onSelectLibrary: onSelectLibrary,
                                            shouldShowSubBoards: shouldShowSubBoards,
                                            shouldShowLibraryBoards: shouldShowLibraryBoards,
                                            isBoardSelected: isBoardSelected,
                                            onBoardSelectChange: onBoardSelectChange,
                                            getIsBoardDisabled: getIsBoardDisabled,
                                            initialBoardId: initialBoardId,
                                            shouldExpandLibraryInsteadOfSelect: shouldExpandLibraryInsteadOfSelect
                                        }, library.id))
                                ]
                            })
                        ]
                    }),
                    children
                ]
            })
        ]
    });
});
PrivateBoardSearch.displayName = "PrivateBoardSearch";
